import { forwardRef, useCallback, useMemo } from "react";
import type { ComponentPropsWithoutRef, KeyboardEvent, MouseEvent } from "react";

import { useMoneyVisibility } from "@/contexts/MoneyVisibilityContext";
import { cn } from "@/lib/utils";

type MoneyValueProps = {
  value: number;
  prefix?: string;
  valueClassName?: string;
  maskedValue?: string;
  asBlock?: boolean;
  'data-cy'?: string;
} & Omit<ComponentPropsWithoutRef<"span">, 'data-cy'>;

const currencyFormatter = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

export const MoneyValue = forwardRef<HTMLSpanElement, MoneyValueProps>(
  (
    {
      value,
      prefix = "",
      className,
      valueClassName,
      maskedValue,
      asBlock = false,
      "data-cy": dataCy,
      onClick,
      onKeyDown,
      ...rest
    },
    ref,
  ) => {
    const { showMoneyValues, toggleShowMoneyValues } = useMoneyVisibility();

    const formatted = useMemo(() => currencyFormatter.format(Number(value) || 0), [value]);
    const fallbackMasked = useMemo(
      () => Array.from(formatted).map((char) => (char === " " ? " " : "*")).join(""),
      [formatted],
    );
    const resolvedMasked = maskedValue ?? fallbackMasked;
    const displayValue = `${prefix}${formatted}`;
    const maskedDisplay = `${prefix}${resolvedMasked}`;

    const label = showMoneyValues ? "Ocultar valores monetários" : "Mostrar valores monetários";

    const accessibleLabel = showMoneyValues ? displayValue : "Valor oculto";

    const handleClick = useCallback(
      (event: MouseEvent<HTMLSpanElement>) => {
        onClick?.(event);
        if (event.defaultPrevented) return;
        toggleShowMoneyValues();
      },
      [onClick, toggleShowMoneyValues],
    );

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLSpanElement>) => {
        onKeyDown?.(event);
        if (event.defaultPrevented) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          toggleShowMoneyValues();
        }
      },
      [onKeyDown, toggleShowMoneyValues],
    );

    return (
      <span
        ref={ref}
        role="button"
        tabIndex={0}
        aria-pressed={showMoneyValues}
        title={label}
        className={cn(
          asBlock ? "flex items-center" : "inline-flex items-center",
          "gap-1 cursor-pointer select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          className,
        )}
        data-cy={dataCy}
        aria-label={accessibleLabel}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        {...rest}
      >
        <span className={cn("relative tabular-nums", valueClassName)}>
          <span className={cn("transition-opacity", showMoneyValues ? "opacity-100" : "opacity-0")}>{displayValue}</span>
          {!showMoneyValues && (
            <span className="absolute inset-0 flex items-center" aria-hidden="true">
              {maskedDisplay}
            </span>
          )}
        </span>
      </span>
    );
  },
);

MoneyValue.displayName = "MoneyValue";
